import React, { PureComponent } from 'react'
import ReactDOMServer from 'react-dom/server'
import { reaction } from 'mobx'
import * as R from 'ramda'
import { Virtuoso } from 'react-virtuoso'
import i18n from 'i18next'
import { observer, Observer } from 'mobx-react'

import { getQuestionTitle } from '../../../../../utils/QuestionHelper'
import ReportPageTitle from '../ReportPageTitle'
import Panel from '../../../../../UniformComponents/Panel'
import Badge from '../../../../../UniformComponents/Badge'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import DebugInfo from '../DebugInfo'
import AiOverview from './AiOverview'
import Button from '../../../../../UniformComponents/Button'
import Typography from '../../../../../UniformComponents/Typography'
import Tooltip from '../../../../../UniformComponents/Tooltip'

@observer
class TextQuestion extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            numberOfResponses: R.pathOr(0, [props.dataKey, 'numberOfResponses'])(props.reportStore.reportData.questions),
            responses: [],
            isLoading: false,
            isOpen: false,
            sentiment: {},
            sentimentFilter: new Set(),
            coding: {},
            codingFilter: new Set(),
            aiState: '',
            generatingSummary: false,
            collapsedHeight: null,
        }

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                const stateCopy = { ...this.state }
                console.log(JSON.stringify(props.data))
                stateCopy.numberOfResponses = props.reportStore.reportData.questions[props.dataKey].numberOfResponses[0]
                this.setState(stateCopy, () => {
                    this.getReportData()
                })
            },
        )

        props.reportStore.verbatim[props.pageQuestionId] = this.renderPDF
        props.reportStore.questionSorting[props.dataKey] = this.getQuestionSorting
    }

    componentDidMount() {
        this.getReportData()
    }

    componentWillUnmount() {
        const { reportStore, pageQuestionId, dataKey } = this.props
        Reflect.deleteProperty(reportStore.verbatim, pageQuestionId)
        Reflect.deleteProperty(reportStore.questionSorting, dataKey)
    }

    loadCoding = async () => {
        const { question, reportStore } = this.props
        const { surveyId, appStore } = reportStore

        try {
            await appStore.http.post(`/report/v1/${surveyId}/textquestion/${question.id}/code`, JSON.stringify({}))
        } catch (e) {
            if (e.status === 400 && e.message?.message) return e.message.message
            console.error(e)
        }
        return null
    }

    loadSentiment = async () => {
        const { question, reportStore } = this.props
        const { surveyId, appStore } = reportStore

        try {
            await appStore.http.post(`/report/v1/${surveyId}/textquestion/${question.id}/sentiment`, JSON.stringify({}))
        } catch (e) {
            if (e.status === 400 && e.message?.message) return e.message.message
            console.error(e)
        }
        return null
    }

    loadThemeAndSentiment = async () => {
        const { reportStore } = this.props
        const { appStore, generatingSentiment } = reportStore
        const isExploriLite = appStore.survey?.metadata.isRapidSurvey

        if (generatingSentiment || isExploriLite) return

        try {
            reportStore.generatingSentiment = true
            this.setState({ generatingSummary: true })
            reportStore.trackAiOpenTextGenerated()
            const codingError = await this.loadCoding()
            const sentimentError = await this.loadSentiment()
            const errorStr = codingError || sentimentError
            if (codingError && sentimentError) { // if just one of the two updated, skip error notification
                appStore.addNotification(null, i18n.t(`report.verbatim.aiCoding.rateLimit.${errorStr}`), 'error')
            }
            await this.getReportData()
        } catch (e) {
            console.error(e)
            appStore.addNotification(null, 'Error loading themes and sentiment', 'error')
        }

        reportStore.generatingSentiment = false
        this.setState({ generatingSummary: false })
    }

    getQuestionSorting = () => {
        const { responses } = this.state
        return [
            Array.from(Array(responses.length).keys()),
            'bar',
            responses.map(({ numberOfResponses }) => numberOfResponses),
            responses.map(({ label }) => label),
        ]
    }

    getReportData = async () => {
        const { question, reportStore } = this.props
        const { isOpen } = this.state
        const { questionTypeCode } = question
        const { appStore } = reportStore
        try {
            const request = JSON.stringify({
                questionId: question.id,
                questionType: questionTypeCode,
                questionOptionId: null,
                filter: {
                    state: reportStore.getReportStates(),
                    surveyDataIds: reportStore.getSurveyDataIdFilter(reportStore.filters.openEnded, 'include'),
                    excludeSurveyDataIds: reportStore.getSurveyDataIdFilter(reportStore.filters.openEnded, 'exclude'),
                    ...R.omit(['openEnded'], reportStore.filters),
                },
            })

            const response = await appStore.http.post(`/report/v1/${reportStore.surveyId}/textquestion`, request)

            const responses = R.sortWith([
                R.descend(R.prop('numberOfResponses')),
                R.ascend(R.prop('label')),
            ], response.labels.map((label, index) => ({
                label,
                numberOfResponses: R.pathOr(0, ['numberOfResponses', index], response),
                surveyDataIds: R.pathOr([], ['surveyDataIds', index], response),
                sentiment: R.pathOr('', ['sentiment', index], response),
                coding: new Set(R.pathOr([], ['categories', index], response)),
            })))

            const questionSentiment = responses.reduce((map, { numberOfResponses, sentiment }) => {
                if (R.isNil(sentiment) || R.isEmpty(sentiment)) return map

                map[sentiment] += numberOfResponses
                return map
            }, {
                POSITIVE: 0,
                NEGATIVE: 0,
                NEUTRAL: 0,
            })

            reportStore.openEnded.set(question.id, responses)

            const questionCoding = responses.reduce((map, { coding, numberOfResponses }) => {
                coding.forEach((c) => {
                    map[c] ??= 0
                    map[c] += numberOfResponses
                })
                return map
            }, {})

            const otherKey = 'Other'
            const other = R.pick([otherKey], questionCoding)
            const sortedQuestionCoding = Object.entries(R.omit([otherKey], questionCoding))
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => ({
                    name: k,
                    value: v,
                }))

            const numberOfResponses = response.numberOfResponses.reduce((a, b) => a + b, 0)
            const numberOfSentimentResponses = Object.values(questionSentiment).reduce((a, b) => a + b, 0)
            let aiState

            const minNumberOfResponses = 20
            const minNumberOfNewResponses = 10

            if (numberOfResponses < minNumberOfResponses) {
                // not enough responses go generate coding
                aiState = 'noData'
            } else if (numberOfSentimentResponses === 0) {
                // enough data to generate, but not yet done
                aiState = 'notGenerated'
            } else if (numberOfResponses === numberOfSentimentResponses) {
                // up-to-date as number of responses and sentiment responses is equal
                aiState = 'upToDate'
            } else if ((numberOfResponses - numberOfSentimentResponses) >= minNumberOfNewResponses) {
                // data is out-of-date, but has enough new responses to generate (>= 10)
                aiState = 'generate'
            } else {
                // not enough new responses (< 10) to generate updated coding
                aiState = 'outOfDate'
            }

            this.setState({
                numberOfResponses,
                aiState,
                responses,
                isLoading: false,
                sentiment: questionSentiment,
                sentimentFilter: new Set(),
                isOpen,
                coding: [...sortedQuestionCoding, { name: otherKey, value: other[otherKey] ?? 0 }],
                codingFilter: new Set(),
            })
        } catch (e) {
            console.log(`Error getting getReportData -> ${e}`)
        }
    }

    addVerbatim = (e) => {
        const { reportStore } = this.props
        const { responses } = this.state
        const index = parseInt(e.currentTarget.attributes.getNamedItem('data-index').value, 10)
        reportStore.useVerbatimFilter(responses.surveyDataIds[index], responses.labels[index])
    }

    getSentiment = (label) => {
        const { sentiment } = this.state
        return sentiment?.[label.trim()]
    }

    toggleCodingFilter = (code) => {
        const { codingFilter } = this.state

        const clonedSet = new Set(codingFilter)
        console.log('toggleCodingFilter', codingFilter)

        if (clonedSet.has(code)) {
            clonedSet.delete(code)
        } else {
            clonedSet.add(code)
        }

        this.setState({
            codingFilter: clonedSet,
        })
    }

    renderCoding = () => {
        const { coding, codingFilter } = this.state

        if (Object.values(coding).reduce((a, { value }) => a + value, 0) === 0) return null

        return (
            <div className="codingSummary">
                <Typography type="h3">Filter by Theme:</Typography>
                <div>
                    {coding.map(({ name, value }) => (
                        <Badge
                            isRounded
                            label={`${name} (${value})`}
                            type={codingFilter.has(name) ? 'success' : 'grey'}
                            onClick={() => this.toggleCodingFilter(name)}
                        />
                    ))}
                </div>
            </div>
        )
    }

    toggleSentimentFilter = (sentiment) => {
        const { sentimentFilter } = this.state

        const clonedSet = new Set(sentimentFilter)

        if (clonedSet.has(sentiment)) {
            clonedSet.delete(sentiment)
        } else {
            clonedSet.add(sentiment)
        }

        this.setState({
            sentimentFilter: clonedSet,
        })
    }

    renderSentiment = (sentiment) => {
        let icon = null

        switch (sentiment) {
            case 'NEUTRAL':
                icon = 'fa-light fa-dash'
                break
            case 'POSITIVE': {
                icon = 'fa-regular fa-thumbs-up'
                break
            }
            case 'NEGATIVE': {
                icon = 'fa-regular fa-thumbs-down'
                break
            }
            default:
        }

        return icon === null
            ? null
            : (
                <div
                    className={`sentimentIcon ${sentiment.toLowerCase()}`}
                >
                    <i className={`${icon}`} />
                </div>
            )
    }

    renderFilter = (label, surveyDataIds) => {
        const { reportStore, question } = this.props
        const { showQuickFilter, filterByOpenEnded, quickFilters } = reportStore
        const hasFilter = quickFilters.has(`${question.id}_${label}`)
        const className = hasFilter
            ? 'fa-regular fa-square-check'
            : 'fa-regular fa-square'

        if (!showQuickFilter) return null

        return (
            <div className="quick-filter-check-container">
                <i
                    onClick={() => filterByOpenEnded(question, label, surveyDataIds)}
                    className={className}
                    style={{ marginRight: '5px' }}
                />
            </div>
        )
    }

    renderRow = (
        isOpen,
        showQuestionSentiment,
        index,
        { label, numberOfResponses, surveyDataIds, sentiment },
    ) => (
        <div className="textResponse" data-index={index} style={{ marginBottom: '8px', marginRight: isOpen ? '8px' : 0}}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: '8px' }}>
                {this.renderFilter(label, surveyDataIds)}
                {showQuestionSentiment && this.renderSentiment(sentiment)}
                <span style={{ flex: 1 }}>{label}</span>
                <span className="responseCount">
                    {numberOfResponses}
                </span>
            </div>
        </div>
    )

    filteredRows = () => {
        const { responses, codingFilter, sentimentFilter } = this.state

        const showRow = ({ coding, sentiment }) => (
            (codingFilter.size === 0 || codingFilter.intersection(coding).size > 0)
            && (
                sentimentFilter.size === 0 || sentimentFilter.has(sentiment)
            )
        )

        return responses.filter(showRow)
    }

    resultRows = (showQuestionSentiment) => {
        const { isOpen } = this.state

        return (
            <div style={{ height: '550px' }}>
                <Virtuoso
                    style={{ height: '100%' }}
                    data={this.filteredRows()}
                    itemContent={(index, row) => (
                        <Observer>
                            {() => this.renderRow(isOpen, showQuestionSentiment, index, row)}
                        </Observer>
                    )}
                />
            </div>
        )
    }

    renderPDF = () => (
        ReactDOMServer.renderToStaticMarkup((
            <div>
                {this.filteredRows().map((row, index) => this.renderRow(true, false, index, row))}
            </div>
        ))
    )

    toggleOpen = () => {
        const stateCopy = { ...this.state }
        stateCopy.isOpen = !stateCopy.isOpen
        this.setState(stateCopy)
    }

    numberOfResponses = () => (
        <div>
            <Panel cssClass="kpi stats">
                <div className="iconCircle">
                    <i className="fa-regular fa-messages" />
                </div>
                <div className="details">
                    <label>Total responses</label>
                    <span>{this.state.numberOfResponses}</span>
                </div>
            </Panel>
        </div>
    )

    generateSentimentSummary = () => {
        const { sentiment } = this.state
        const total = Object.values(sentiment).reduce((a, b) => a + b, 0)

        return {
            POSITIVE: {
                pct: ((sentiment.POSITIVE ?? 0) * 100 / (total || 1)),
                total: sentiment.POSITIVE,
            },
            NEGATIVE: {
                pct: ((sentiment.NEGATIVE ?? 0) * 100 / (total || 1)),
                total: sentiment.NEGATIVE,
            },
            NEUTRAL: {
                pct: ((sentiment.NEUTRAL ?? 0) * 100 / (total || 1)),
                total: sentiment.NEUTRAL,
            },
        }
    }

    sentiment = () => {
        const { sentimentFilter } = this.state
        const { reportStore } = this.props
        const { appStore } = reportStore

        const sentimentSummary = this.generateSentimentSummary()
        const showSentiment = Object.entries(sentimentSummary).some(([, { total }]) => total > 0)

        return (
            <div>
                {showSentiment && (
                    <div
                        className="sentimentSummary"
                    >
                        {Object.entries(sentimentSummary).map(([key, { pct, total }]) => (
                            <Panel
                                cssClass={`${key.toLocaleLowerCase()} ${sentimentFilter.has(key) ? 'active' : ''}`}
                                onClick={() => this.toggleSentimentFilter(key)}
                            >
                                {this.renderSentiment(key)}
                                <div>
                                    <div className="header">{i18n.t(`sentiment.${key}`)}</div>
                                    {pct.toFixed(0)}
                                    %
                                    <div className="footer">
                                        ({total} responses)
                                    </div>
                                </div>
                            </Panel>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    showCodingAndSentiment = (isExploriLite) => {
        const { reportStore, question } = this.props
        return reportStore.isAiSummaryEnabled() && !question.isAiCodingDisabled && !isExploriLite
    }

    renderGenerateBtn = (aiState, isSurveyClosed, generatingSummary, isReadOnlyUser) => {
        const { reportStore } = this.props
        const { generatingSentiment } = reportStore
        const isDisabled = generatingSentiment || isReadOnlyUser
        const btnText = 'Generate themes and sentiment'

        const buttonEl = (
            <Button
                label={btnText}
                cssClass="regular"
                variant="secondary"
                icon="fas fa-sparkles"
                onClick={this.loadThemeAndSentiment}
                disabled={isDisabled}
            />
        )

        if (isReadOnlyUser) {
            return (
                <Tooltip
                    text={i18n.t('report.verbatim.aiCoding.generate.insufficientRights')}
                    direction="bottom"
                    cssClass="offset-down"
                    isNew
                >
                    {buttonEl}
                </Tooltip>
            )
        }

        let showBtn = false
        if (['generate', 'notGenerated'].includes(aiState)) showBtn = true
        if (isSurveyClosed && ['outOfDate', 'upToDate'].includes(aiState)) showBtn = true
        return !generatingSummary && showBtn ? buttonEl : null
    }

    render() {
        const { store, reportStore, question } = this.props
        const { isLoading, isOpen, numberOfResponses, aiState, generatingSummary } = this.state
        const { survey, userData } = store
        const summary = R.pathOr(null, ['questions', question.pageQuestionId.toLowerCase()], reportStore.AiSummary)
        const showSummary = reportStore.generatingSummary || summary
        const isSurveyClosed = survey.metadata.status === 'Closed'
        const isExploriLite = store.survey?.metadata.isRapidSurvey
        const isReadOnlyUser = [150, 170].includes(store.userData.role)
        const totalDisplayed = this.filteredRows().reduce((acc, cur) => cur.numberOfResponses + acc, 0)

        const showQuestionSentiment = userData.flags.isAiSummaryEnabled
            && survey.metadata.isSurveyAiSummaryEnabled
            && !question.isAiCodingDisabled
            && !isExploriLite


        return (
            <Panel cssClass="question verbatim">
                <div id={`questionData_${question.pageQuestionId}`}>
                    <PanelHeader>
                        <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                            <ReportPageTitle
                                h2={getQuestionTitle({
                                    content: question.content.shortTitle,
                                    locale: reportStore.locale,
                                    defaultLocale: survey.metadata.defaultLocale,
                                    initialLocale: question.metadata.initialLocale,
                                })}
                                h3={getQuestionTitle({
                                    content: question.content.longTitle,
                                    locale: reportStore.locale,
                                    defaultLocale: survey.metadata.defaultLocale,
                                    initialLocale: question.metadata.initialLocale,
                                })}
                                reportStore={reportStore}
                                questionTypeCode={question.questionTypeCode}
                            />
                            {this.numberOfResponses()}
                        </div>
                    </PanelHeader>
                    <DebugInfo
                        store={store}
                        questionTypeCode={question.questionTypeCode}
                        reportStore={reportStore}
                        questionId={question.id}
                        pageQuestionId={question.pageQuestionId}
                    />
                    {(['TX', 'TA'].includes(question.questionTypeCode) && this.showCodingAndSentiment(isExploriLite)) && (
                        <AiOverview
                            store={{ generatingSummary }}
                            summary={i18n.t(`report.verbatim.aiCoding.${aiState}${isSurveyClosed ? '.closedSurvey' : ''}`)
                        }>
                            {this.renderGenerateBtn(aiState, isSurveyClosed, generatingSummary, isReadOnlyUser)}
                        </AiOverview>
                    )}
                    {question.questionTypeCode === 'DT' && showSummary && <AiOverview store={reportStore} summary={summary} />}
                    {this.showCodingAndSentiment(isExploriLite) && (
                        <>
                            {this.renderCoding()}
                            {this.sentiment()}
                        </>
                    )}
                    {isLoading && <div className="loader" />}
                    {!isLoading && (
                        <div className={`textResponseList ${isOpen ? '' : 'collapsed'}`}>
                            {this.resultRows(showQuestionSentiment)}
                        </div>
                    )}
                    <div
                        className="textResponseToggle"
                        style={{ overflowAnchor: 'none' }}
                        onClick={this.toggleOpen}
                    >
                        { isOpen ? 'Show less' : `Show all ${totalDisplayed} responses` }
                    </div>
                </div>
            </Panel>
        )
    }
}

export default TextQuestion
